import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useRef, useState } from 'react'
import { aiProxyBase } from '~utils/base-url'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
}

interface UseChatStreamOptions {
  onError?: (error: Error) => void
}

export function useChatStream(options?: UseChatStreamOptions) {
  const { getAccessTokenSilently } = useAuth0()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const messagesRef = useRef<ChatMessage[]>([])
  messagesRef.current = messages

  const sendMessage = useCallback(
    async (userMessage: string) => {
      if (!userMessage.trim() || isStreaming) return

      const newUserMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: userMessage,
      }
      setMessages((prev) => [...prev, newUserMessage])

      const allMessages = [...messagesRef.current, newUserMessage]

      setIsStreaming(true)
      abortControllerRef.current = new AbortController()

      const assistantMessageId = crypto.randomUUID()

      try {
        const token = await getAccessTokenSilently()

        const response = await fetch(`${aiProxyBase()}/chatbot/completions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            mode: 'aida',
            messages: allMessages.map((m) => ({
              role: m.role,
              content: m.content,
            })),
            temperature: 0,
            top_p: 1,
            stream: true,
            searchIndex: 'insite-index-v1',
          }),
          signal: abortControllerRef.current.signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        if (!response.body) {
          throw new Error('Response body is null')
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let assistantMessage = ''

        setMessages((prev) => [
          ...prev,
          { id: assistantMessageId, role: 'assistant', content: '' },
        ])

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          const chunk = decoder.decode(value, { stream: true })
          const lines = chunk.split('\n')

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              if (data === '[DONE]') continue

              try {
                const parsed = JSON.parse(data)
                const content = parsed.choices?.[0]?.delta?.content
                if (content) {
                  assistantMessage += content
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, content: assistantMessage }
                        : msg,
                    ),
                  )
                }
              } catch {
                // Skip invalid JSON lines
              }
            }
          }
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          return
        }
        options?.onError?.(error as Error)
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId && msg.content === ''
              ? {
                  ...msg,
                  content: 'Sorry, something went wrong. Please try again.',
                }
              : msg,
          ),
        )
      } finally {
        setIsStreaming(false)
        abortControllerRef.current = null
      }
    },
    [getAccessTokenSilently, isStreaming, options],
  )

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort()
    setIsStreaming(false)
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  return {
    messages,
    isStreaming,
    sendMessage,
    stopStreaming,
    clearMessages,
  }
}
